import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { useClinic } from '@/hooks/use-clinic'
import { getFollowUpBucket, getReminderStatus, getStats } from '@/lib/clinic/selectors'
import { formatDate, formatTime, formatWeekday, dateFromTimestamp } from '@/lib/clinic/dates'
import { buttonVariants } from '@/components/ui/button'
import { PatientAvatar } from '@/components/app/patient-avatar'
import { FollowUpBadge, ReminderBadge, VisitTypeBadge } from '@/components/app/status-badges'
import { DetailItem } from '@/components/app/common'

import {
    CalendarClock,
    ClipboardPlus,
    FilePlus2,
    HeartPulse,
    MessageCircle,
    Phone,
    TriangleAlert,
    UserRound
} from 'lucide-react'

import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle
} from '@/components/ui/sheet'

import {
    GENDER_LABELS,
    ageLabel,
    formatPhone,
    getPatientAge
} from '@/lib/clinic/format'

interface PatientQuickViewProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    patientId: string | null
    onContact: (patientId: string, channel: 'whatsapp' | 'call') => void
    onAddFollowUp: (patientId: string) => void
}

export const PatientQuickView: FC<PatientQuickViewProps> = ({
    open,
    onOpenChange,
    patientId,
    onContact,
    onAddFollowUp
}): ReactNode => {
    const { index, today } = useClinic()
    const patient = patientId ? index.patientsById.get(patientId) ?? null : null
    const stats = patient ? getStats(index, patient.id) : null
    const lastVisit = patient ? index.visitsByPatient.get(patient.id)?.[0] ?? null : null
    const followUp = stats?.pendingFollowUp ?? null
    const close = (): void => onOpenChange(false)

    return (
        <Sheet open={open} onOpenChange={next => onOpenChange(next)}>
            <SheetContent side='right' className='gap-0 overflow-y-auto p-0 data-[side=right]:w-full data-[side=right]:sm:max-w-md'>
                {patient && stats ? (
                    <>
                        <SheetHeader className='gap-3 border-b bg-muted/40 p-5 pr-12'>
                            <div className='flex items-center gap-3'>
                                <PatientAvatar name={patient.name} seed={patient.id} size='lg' />

                                <div className='min-w-0'>
                                    <SheetTitle className='truncate text-lg font-semibold'>
                                        {patient.name}
                                    </SheetTitle>

                                    <SheetDescription className='flex flex-wrap items-center gap-x-2 gap-y-1'>
                                        <span className='font-medium text-foreground/80'>{patient.id}</span>
                                        <span aria-hidden='true'>·</span>
                                        <span>{ageLabel(getPatientAge(patient, today))}</span>
                                        {patient.gender && (
                                            <>
                                                <span aria-hidden='true'>·</span>
                                                <span>{GENDER_LABELS[patient.gender]}</span>
                                            </>
                                        )}
                                    </SheetDescription>
                                </div>
                            </div>

                            <div className='flex flex-wrap items-center gap-2'>
                                <VisitTypeBadge totalVisits={stats.totalVisits} />

                                <span className='text-sm font-medium text-foreground tabular-nums'>
                                    {formatPhone(patient.phone)}
                                </span>

                                <div className='ml-auto flex gap-1.5'>
                                    <button
                                        type='button'
                                        onClick={() => onContact(patient.id, 'whatsapp')}
                                        className={cn(buttonVariants({ variant: 'outline', size: 'icon-sm' }), 'text-emerald-700')}
                                        aria-label={`Send WhatsApp message to ${patient.name}`}
                                    >
                                        <MessageCircle />
                                    </button>

                                    <button
                                        type='button'
                                        onClick={() => onContact(patient.id, 'call')}
                                        className={cn(buttonVariants({ variant: 'outline', size: 'icon-sm' }), 'text-sky-700')}
                                        aria-label={`Call ${patient.name}`}
                                    >
                                        <Phone />
                                    </button>
                                </div>
                            </div>
                        </SheetHeader>

                        <div className='grid gap-5 p-5'>
                            {(patient.allergies || patient.medicalHistory) && (
                                <div className='grid gap-2'>
                                    {patient.allergies && (
                                        <div className='flex gap-2.5 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900'>
                                            <TriangleAlert className='mt-0.5 size-4 shrink-0' aria-hidden='true' />

                                            <p>
                                                <span className='font-semibold'>Allergies: </span>
                                                {patient.allergies}
                                            </p>
                                        </div>
                                    )}

                                    {patient.medicalHistory && (
                                        <div className='flex gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950'>
                                            <HeartPulse className='mt-0.5 size-4 shrink-0' aria-hidden='true' />

                                            <p>
                                                <span className='font-semibold'>Medical history: </span>
                                                {patient.medicalHistory}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            <dl className='grid grid-cols-3 gap-3 rounded-xl border p-3'>
                                <DetailItem label='Registered' value={formatDate(dateFromTimestamp(patient.createdAt))} />
                                <DetailItem label='Last visit' value={formatDate(stats.lastVisit)} />
                                <DetailItem label='Total visits' value={stats.totalVisits} />
                            </dl>

                            <div className='grid gap-2'>
                                <p className='text-xs font-semibold tracking-wide text-muted-foreground uppercase'>
                                    Next follow-up
                                </p>

                                {followUp ? (
                                    <div className='grid gap-2 rounded-xl border p-3'>
                                        <div className='flex flex-wrap items-center gap-2'>
                                            <FollowUpBadge bucket={getFollowUpBucket(followUp, today)} />
                                            <ReminderBadge status={getReminderStatus(followUp, index)} />
                                        </div>

                                        <p className='text-sm font-semibold text-foreground'>
                                            {formatWeekday(followUp.dueDate)}
                                            {followUp.dueTime ? `, ${formatTime(followUp.dueTime)}` : ''}
                                        </p>

                                        {followUp.reason && (
                                            <p className='text-sm text-muted-foreground'>
                                                {followUp.reason}
                                            </p>
                                        )}
                                    </div>
                                ) : (
                                    <div className='flex items-center justify-between gap-3 rounded-xl border border-dashed p-3'>
                                        <p className='text-sm text-muted-foreground'>
                                            No follow-up scheduled.
                                        </p>

                                        <button
                                            type='button'
                                            onClick={() => onAddFollowUp(patient.id)}
                                            className={buttonVariants({ variant: 'outline', size: 'sm' })}
                                        >
                                            <CalendarClock />
                                            Add
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className='grid gap-2'>
                                <p className='text-xs font-semibold tracking-wide text-muted-foreground uppercase'>
                                    Last visit
                                </p>

                                {lastVisit ? (
                                    <div className='grid gap-1 rounded-xl border p-3'>
                                        <p className='text-xs text-muted-foreground'>
                                            {formatDate(lastVisit.date)}
                                            {lastVisit.toothNumbers ? ` · Tooth ${lastVisit.toothNumbers}` : ''}
                                        </p>

                                        <p className='text-sm font-semibold text-foreground'>
                                            {lastVisit.complaint}
                                        </p>

                                        {lastVisit.treatment && (
                                            <p className='text-sm text-muted-foreground'>
                                                {lastVisit.treatment}
                                            </p>
                                        )}
                                    </div>
                                ) : (
                                    <p className='rounded-xl border border-dashed p-3 text-sm text-muted-foreground'>
                                        No visits recorded yet.
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className='mt-auto grid gap-2 border-t bg-white p-5 sm:grid-cols-3'>
                            <Link href={`/patients/${patient.id}`} onClick={close} className={cn(buttonVariants(), 'sm:col-span-3')}>
                                <UserRound />
                                Open patient profile
                            </Link>

                            <Link href={`/patients/${patient.id}/visits/new`} onClick={close} className={cn(buttonVariants({ variant: 'outline' }), 'sm:col-span-1')}>
                                <ClipboardPlus />
                                New visit
                            </Link>

                            <Link href={`/prescriptions/new?patient=${patient.id}`} onClick={close} className={cn(buttonVariants({ variant: 'outline' }), 'sm:col-span-2')}>
                                <FilePlus2 />
                                Create prescription
                            </Link>
                        </div>
                    </>
                ) : (
                    <SheetHeader>
                        <SheetTitle>Patient not found</SheetTitle>
                        <SheetDescription>This patient may have been deleted.</SheetDescription>
                    </SheetHeader>
                )}
            </SheetContent>
        </Sheet>
    )
}
